<template>
  <div class="flex w-full" v-bind="getPt('root', passThrough, ptCtx)">
    <input
      type="file"
      ref="uploader"
      @change="handleFile"
      multiple
      :accept="acceptedFileFormats"
      class="hidden"
      v-bind="getPt('input', passThrough, ptCtx)"
    />
    <div class="w-full" v-bind="getPt('container', passThrough, ptCtx)">
      <div
        class="flex flex-col w-full"
        v-bind="getPt('dropzoneWrapper', passThrough, ptCtx)"
      >
        <div
          class="w-full group"
          :class="[
            {
              'cursor-no-drop opacity-60': disable,
              'cursor-pointer': !disable,
            },
            getPt('dropzone', passThrough, ptCtx)?.class,
          ]"
          @click="
            () => {
              pickFile();
            }
          "
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @dragenter.prevent
          @drop.prevent="
            (e) => {
              handleFile(e, true);
              isDragging = false;
            }
          "
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
        >
          <slot
            name="uploadAreaContent"
            :is-dragging="isDragging"
            :is-hovering="isHovering"
            :disable="disable"
            :accepted-file-formats="acceptedFileFormats"
            :max-file-limit="maxFileLimit"
          >
            <div
              class="flex flex-col items-center w-full h-full justify-center"
              v-bind="getPt('uploadArea', passThrough, ptCtx)"
            >
              <MeridiaIcon
                class="icon-m transition-all duration-300"
                :class="[
                  {
                    'text-neutral-text-body': !isDragging,
                    'text-primary-surface-default': isDragging,
                    'group-hover:text-primary-surface-default': !disable,
                  },
                  getPt('uploadIcon', passThrough, ptCtx)?.class,
                ]"
                name="cloud_upload"
                type="material-icons-outlined"
              />
              <transition name="fade" mode="out-in">
                <div
                  v-if="!isDragging"
                  class="text-neutral-text-body text-body-large transition-all mt-2"
                  :class="getPt('uploadText', passThrough, ptCtx)?.class"
                >
                  {{
                    $t(
                      'meridia_components.file_upload.select_or_drag_and_drop_files_to_upload'
                    )
                  }}
                </div>
                <div
                  v-else
                  class="text-neutral-text-body text-body-large transition-all mt-2"
                  :class="getPt('uploadText', passThrough, ptCtx)?.class"
                >
                  {{ $t('meridia_components.file_upload.drop_files_here') }}
                </div>
              </transition>
              <div
                class="text-neutral-border-disabled text-body-small mt-xs"
                :class="getPt('uploadSubtitle', passThrough, ptCtx)?.class"
              >
                {{
                  $t('meridia_components.file_upload.upload_subtitle', {
                    formats: acceptedFileFormats,
                    limit: maxFileLimit / 1024 / 1024,
                  })
                }}
              </div>
              <MeridiaButton
                :type="'tertiary'"
                :size="'base'"
                :label="$t('meridia_components.file_upload.upload_file')"
                class="mt-2xs"
                :class="getPt('uploadButton', passThrough, ptCtx)?.class"
              />
            </div>
          </slot>
        </div>
      </div>
      <transition-group
        name="fade"
        mode="out-in"
        :duration="150"
        tag="div"
        class="gap-y-xs flex"
        :class="getPt('list', passThrough, ptCtx)?.class"
      >
        <div
          v-for="(fileObject, index) in files"
          :key="fileObject.id"
          class="p-3 w-full flex justify-center items-center radius-base bg-white"
          :class="
            getPt('item', passThrough, { ...ptCtx, file: fileObject, index })
              ?.class
          "
        >
          <div
            class="inline-flex justify-between w-full items-center"
            v-bind="
              getPt('itemContent', passThrough, {
                ...ptCtx,
                file: fileObject,
                index,
              })
            "
          >
            <div
              class="inline-flex items-center mr-xs flex-1 overflow-hidden"
              v-bind="
                getPt('itemInfo', passThrough, {
                  ...ptCtx,
                  file: fileObject,
                  index,
                })
              "
            >
              <transition name="fade" mode="out-in" :duration="150">
                <MeridiaIcon
                  v-if="fileObject.status === 'success'"
                  class="icon-s text-primary-surface-default p-xs"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="check"
                  type="material-icons-outlined"
                />
                <MeridiaIcon
                  v-else-if="fileObject.status === 'error'"
                  class="icon-s text-critical-surface-default p-xs"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="block"
                  type="material-icons-outlined"
                />
                <MeridiaIcon
                  v-else-if="fileObject.status === 'pending'"
                  class="icon-s text-primary-surface-default p-xs animate-spin"
                  :class="
                    getPt('statusIcon', passThrough, {
                      ...ptCtx,
                      file: fileObject,
                      index,
                    })?.class
                  "
                  name="progress_activity"
                  type="material-symbols-outlined"
                />
              </transition>
              <span
                class="text-neutral-text-body text-body-base truncate"
                :class="
                  getPt('fileName', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                >{{ fileObject.file.name }}</span
              >
              <div
                class="h-m w-px bg-neutral-border-subtle mx-2xs"
                :class="
                  getPt('divider', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
              />
              <span
                v-if="fileObject.status === 'success'"
                class="text-neutral-border-disabled text-body-base"
                :class="
                  getPt('fileSize', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                >{{ humanStorageSize(fileObject.file.size) }}</span
              >
              <span
                v-else-if="fileObject.status === 'error'"
                class="text-critical-label-default text-body-base whitespace-nowrap"
                :class="
                  getPt('errorText', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                >{{ humanStorageSize(fileObject.file.size) }} •
                {{
                  fileObject.reason
                    ? $t('meridia_components.file_upload.' + fileObject.reason)
                    : $t('meridia_components.file_upload.upload_failed')
                }}</span
              >
            </div>
            <div
              class="inline-flex items-center"
              v-bind="
                getPt('itemActions', passThrough, {
                  ...ptCtx,
                  file: fileObject,
                  index,
                })
              "
            >
              <MeridiaButton
                v-if="fileObject.status === 'success' && !disableDownload"
                :type="'tertiary'"
                :size="'base'"
                icon="open_in_new"
                :class="
                  getPt('downloadButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                @click.prevent="handleDownload(fileObject)"
              />
              <MeridiaButton
                v-if="fileObject.status === 'pending'"
                :type="'tertiary'"
                :size="'base'"
                :class="
                  getPt('cancelButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })?.class
                "
                @click.prevent="cancelUpload(fileObject)"
              >
                <template #customIcon>
                  <MeridiaIcon
                    name="cancel"
                    type="material-symbols-outlined"
                    style="font-size: inherit"
                  />
                </template>
              </MeridiaButton>
              <MeridiaButton
                v-else
                :type="'tertiary'"
                :size="'base'"
                v-bind="
                  getPt('deleteButton', passThrough, {
                    ...ptCtx,
                    file: fileObject,
                    index,
                  })
                "
                @click.prevent="deleteFile($event, fileObject)"
              >
                <template #customIcon>
                  <MeridiaIcon
                    name="delete"
                    type="material-symbols-outlined"
                    style="font-size: inherit"
                  />
                </template>
              </MeridiaButton>
            </div>
          </div>
        </div>
      </transition-group>
      <slot name="footer" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, VNode } from 'vue';
import { MeridiaFileType, PassThroughMeridiaFileUpload } from '.';
import MeridiaButton from '../button/MeridiaButton.vue';
import MeridiaIcon from '../icon/MeridiaIcon.vue';
import { usePassThrough } from 'primevue/passthrough';
import { getPt } from '../utils';

/**
 * Holds the selected files for upload.
 * @type {Ref<MeridiaFileType>}
 */
const files = defineModel<MeridiaFileType[]>({ required: true });

const props = withDefaults(
  defineProps<{
    acceptedFileFormats: string;
    maxFileLimit?: number | undefined;
    disable?: boolean;
    disableDownload?: boolean;
    /** Per-element passthrough — see PassThroughMeridiaFileUpload. */
    pt?: PassThroughMeridiaFileUpload;
  }>(),
  {
    maxFileLimit: 5368709120,
    disableDownload: false,
  }
);

const emit = defineEmits<{
  (
    event: 'createFile',
    file: File,
    callback: (response: {
      status: boolean;
      reason: string | undefined;
      /** Restamp the row id (consumers with numeric backend ids). Omit/undefined
       *  to keep the existing client id. */
      id: number | undefined;
      /** Backend resource id (e.g. UUID) stored on the row for later delete. */
      serverId?: string;
    }) => Promise<void>
  ): void;
  (
    event: 'deleteFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  (
    event: 'cancelUpload',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
  (
    event: 'downloadFile',
    fileObject: MeridiaFileType,
    callback: (response: boolean) => Promise<void>
  ): void;
}>();

const slots = defineSlots<{
  uploadAreaContent: (props: {
    isDragging: boolean;
    isHovering: boolean;
    disable: boolean;
    acceptedFileFormats: string;
    maxFileLimit: number;
  }) => VNode;
  footer: () => VNode;
}>();

// Merge consumer `pt` over the (empty) defaults; `getPt(section, passThrough, ctx)`
// then resolves each section in the template (calling function-form sections with
// `ctx`). Mirrors the other Meridia inputs.
const passThrough = usePassThrough(
  props.pt,
  {} as PassThroughMeridiaFileUpload,
  { mergeSections: false, mergeProps: true }
);

const isDragging = ref(false);
const isHovering = ref(false);

/**
 * Live context handed to function-form passthrough sections (dropzone-level).
 * Row sections additionally receive `{ file, index }`, spread in at the call site.
 */
const ptCtx = computed(() => ({
  isDragging: isDragging.value,
  isHovering: isHovering.value,
  disable: !!props.disable,
  acceptedFileFormats: props.acceptedFileFormats,
  maxFileLimit: props.maxFileLimit,
}));

/**
 * Holds the uploader instance.
 * @type {Ref<any>}
 */
const uploader = ref<any>(null);

/**
 * Function to open the file picker dialog.
 */
function pickFile() {
  if (props.disable) return;
  uploader.value.click();
}

function checkSizeValid(auxFiles: FileList) {
  const MAX_SIZE = props.maxFileLimit;
  const EMPTY = 0;
  const filesFormatted = Array.from(auxFiles);

  return filesFormatted.some((file) => {
    if (file.size === EMPTY) {
      files.value.push({
        status: 'error',
        reason: 'empty',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else if (file.size > MAX_SIZE) {
      files.value.push({
        status: 'error',
        reason: 'too_big',
        id: Math.random() * 1000,
        file: file,
      });
      return true;
    } else {
      return false;
    }
  });
}
/**
 * Function to handle the selected file(s) and update the files list.
 * @param {Event} event - The file input change event.
 */
async function handleFile(event: Event, isDrag = false) {
  let eventFiles: { id: number; file: File }[];
  if (isDrag) {
    // convert current files into allFiles array to be able to add new files
    if (!(event as DragEvent)?.dataTransfer?.files) return;

    // add new files to allFiles array
    eventFiles = Array.from(
      (event as DragEvent).dataTransfer?.files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  } else {
    const targetEvent = event.target as HTMLInputElement;
    // convert current files into allFiles array to be able to add new files
    if (!targetEvent.files) return;

    if (targetEvent.files !== null && checkSizeValid(targetEvent.files)) {
      return;
    }
    // add new files to allFiles array
    eventFiles = Array.from(
      (event.target as HTMLInputElement).files as FileList
    ).map((file) => {
      const id = Math.random() * 1000;
      return {
        id: id,
        file: file,
      };
    });
  }

  eventFiles.map(async (fileObject) => {
    files.value.push({
      status: 'pending',
      reason: '',
      id: fileObject.id,
      file: fileObject.file,
    });
  });
  // start new files upload
  await Promise.all(
    eventFiles.map(async (fileObject) => {
      await new Promise((resolve) => {
        emit('createFile', fileObject.file, async (response) => {
          const index = files.value.findIndex((f) => f.id === fileObject.id);
          // The row may have been removed mid-flight (e.g. cancelled).
          if (index === -1) {
            resolve(true);
            return;
          }
          files.value[index] = {
            ...files.value[index],
            status: response.status ? 'success' : 'error',
            reason: response.reason,
            // Keep the existing client id unless the caller restamps it, so the
            // row doesn't re-key (and re-animate) on pending → settled.
            id: response.id ?? files.value[index].id,
            serverId: response.serverId,
            file: fileObject.file,
          };
          resolve(true);
        });
      });
    })
  ).then(() => {
    if (uploader.value) {
      uploader.value.value = '';
    }
  });
}

function handleDownload(fileObject: MeridiaFileType) {
  emit('downloadFile', fileObject, async (response) => {
    if (!response) {
      const index = files.value.findIndex((f) => f.id === fileObject.id);
      if (index !== -1) {
        files.value[index].status = 'error';
        files.value[index].reason = 'download_failed';
      }
    }
  });
}

function cancelUpload(fileObject: MeridiaFileType) {
  emit('cancelUpload', fileObject, async (response) => {
    if (response) {
      const index = files.value.findIndex((f) => f.id === fileObject.id);
      if (index > -1) {
        files.value.splice(index, 1);
      }
    }
  });
}

/**
 * Function to delete a file from the files list.
 * @param {File} file - The file to be deleted.
 * @param {Boolean} failedUpload - failed upload or not.
 */
async function deleteFile(
  e: Event,
  fileObject: MeridiaFileType,
  failedUpload = false
) {
  e.stopPropagation();
  if (!files.value) return;
  if (!failedUpload || fileObject.reason === 'delete_failed') {
    emit('deleteFile', fileObject, async (response) => {
      if (response) {
        const index = files.value.findIndex((f) => f.id === fileObject.id);
        if (index > -1) {
          files.value.splice(index, 1);
        }
      } else {
        const index = files.value.findIndex((f) => f.id === fileObject.id);
        files.value[index].status = 'error';
        files.value[index].reason = 'delete_failed';
      }
    });
  } else {
    const index = files.value.findIndex((f) => f.id === fileObject.id);
    if (index > -1) {
      files.value.splice(index, 1);
    }
  }
}

function humanStorageSize(bytes: number, decimals = 2): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return (
    parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i]
  );
}
</script>
